import {
    Api, Currency, DisplayType, HorizontalAlignment, LogManager, ModelRow, ModelSearchResult, Navigation, StringUtil
} from "@mcleod/core";
import {
    Button, ChangeEvent, Checkbox, ClickEvent, Component, DataDisplayEvent, DataSource, DataSourceMode,
    EditRowDecorator, Image, Label, Layout, Panel, Tab, Table, TableCell, TableColumn, TableRow, TableRowDisplayEvent,
    TableRowMode, Textbox, ValidationResult
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import {
    AppointmentUtil, CarrierOfferInfo, DispatchUtil, isSpotOrder, MultiStop, OfferSource,
    QuickOfferSlideout, QuickSellDetails
} from "@mcleod/dispatch";
import { setCommodityTooltipContent } from "./BrokeragePlanningUtil";
import { MovementCarrierPay } from "./MovementCarrierPay";
import { OfferField } from "./models/ModelCarrierOffer";
import { CarrierAssignmentStepper } from "./CarrierAssignmentStepper";
import { CarrierCapacity } from "./CarrierCapacity";
import { AutogenLayoutAvailableLoads } from "./autogen/AutogenLayoutAvailableLoads";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

const log = LogManager.getLogger("lme.powerbroker.AvailableLoads");

export interface AvailableLoadProps {
    location_city_id: number,
    location_city_name: string,
    location_state: string,
    load_to_city_id?: number,
    load_to_city_name?: string,
    load_to_state?: string,
    cutoff_date: Date,
    trailer_type?: string,
    location_radius: number,
    load_to_radius: number,
    max_loads: number,
    carrier_id: string
}

export interface CarrierAssignmentProps {
    payee_id: string,
    carrier_name: string,
    icc_number: string,
    dot_number: string,
    carrier_contact?: string,
    carrier_phone?: string,
    carrier_email?: string,
    broker_avail_tract: string
}

export class AvailableLoads extends AutogenLayoutAvailableLoads {
    private _availableLoadProps: AvailableLoadProps
    private _carrierAssignmentProps: CarrierAssignmentProps
    private _tabAvailableLoads: Tab;
    private _checkboxSelectAll: Checkbox;
    private _capacityLayout: CarrierCapacity;
    private _sourceCarrier;

    override onLoad() {
        this.tableAvailableLoads.overrideUrlParams = (selectedRow: TableRow) => {
            return { key: selectedRow.data.get("id"), orderId: selectedRow.data.get("orders.id") }
        }
        this.tableAvailableLoads.doOnResolveRowEdit = async (updatedRow: ModelRow) => {
            this.searchForLoads();
            return null;
        };
        this.tableAvailableLoads.onEditLayoutLoaded = this._offerLayoutLoaded();

        this.buttonBlastAllOffers.addClickListener((event: ClickEvent) => this.buttonBlastAllOffersOnClick(event));
        this.configureSelectColumn()
    }

    get availableLoadProps() {
        return this._availableLoadProps;
    }

    set availableLoadProps(props: AvailableLoadProps) {
        this._availableLoadProps = props;
        this.sourceAvailableLoads.searchRow.setValues(props);
        this.panelCenterTools.displayData(this.sourceAvailableLoads.searchRow, null, 0);
    }

    get capacityLayout() {
        return this._capacityLayout;
    }

    set capacityLayout(value: CarrierCapacity) {
        this._capacityLayout = value;
    }


    get carrierAssignmentProps() {
        return this._carrierAssignmentProps;
    }

    set carrierAssignmentProps(value: CarrierAssignmentProps) {
        this._carrierAssignmentProps = value;
    }

    get tabAvailableLoads() {
        return this._tabAvailableLoads;
    }

    set tabAvailableLoads(value: Tab) {
        this._tabAvailableLoads = value;
    }

    get checkboxSelectAll(): Checkbox {
        return this._checkboxSelectAll;
    }

    set checkboxSelectAll(value: Checkbox) {
        this._checkboxSelectAll = value;
    }

    get sourceCarrier() {
        return this._sourceCarrier;
    }

    set sourceCarrier(value: DataSource) {
        this._sourceCarrier = value;
    }

    async searchForLoads() {
        await this.sourceAvailableLoads.search(this.sourceAvailableLoads.searchRow.data).then(
            result => this.updateTab(result)
        );
    }

    buttonRefreshOnClick(event: ClickEvent) {
        if (this.panelCenterTools.validateSimple()) {
            this.searchForLoads();
        }
    }

    updateTab(result: ModelSearchResult) {
        const header = this.tabAvailableLoads.heading;
        const originString = this.availableLoadProps.location_city_name + ", " + " " + this.availableLoadProps.location_state;
        let destinationString: string;
        if (this.availableLoadProps.load_to_city_id == null) {
            destinationString = "ANYWHERE";
        } else {
            destinationString = this.availableLoadProps.load_to_city_name + ", " + " " + this.availableLoadProps.load_to_state;
        }
        const labelOrigin = header.findComponentById("labelOrigin") as Label;
        labelOrigin.text = originString;
        const labelDestination = header.findComponentById("labelDestination") as Label;
        labelDestination.text = destinationString;

        const panelTotal = header.findComponentById("panelTotal") as Panel;
        const valLabel = panelTotal.findComponentById("labelTotalVal") as Label;
        valLabel.text = this.tableAvailableLoads.rowCount.toString();
        this.syncSelectAllCheckbox();
    }

    tableAvailableLoadsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        this.formatDateFields(tableRow)
        const labelStopsPickDrop = tableRow.findComponentById("labelStopsPickDrop") as Label;
        labelStopsPickDrop.applyLinkStyle();
        labelStopsPickDrop.applyButtonHoverStyle();
        const panelCommodity = tableRow.findComponentById("panelCommodity") as Panel;
        if (modelRow.get("commodity.is_hazmat") == "Y") {
            const imageHazmat = tableRow.findComponentById("imageHazmat") as Image;
            imageHazmat.visible = true;

            if (modelRow.get("orders.hazmat_code") != null)
                CommonTooltips.setTooltipContentTextboxes(panelCommodity, () => [
                    {
                        caption: "Hazmat Code",
                        displayType: DisplayType.CURRENCY,
                        text: modelRow.get("orders.hazmat_code")
                    }
                ])
        }
        AppointmentUtil.setAppointmentLabel(tableRow, "labelOrigAppt", "stop_origin.confirmed", "stop_origin.appt_required");
        AppointmentUtil.setAppointmentLabel(tableRow, "labelDestAppt", "stop_dest.confirmed", "stop_dest.appt_required");
        this.setPostedRateVisibility(tableRow);
        this.setTemperatureVisibility(tableRow);
        if (StringUtil.isEmptyString(this.sourceCarrier.activeRow.get("contact.email"))) {
            const cb = tableRow.findComponentById("checkboxSelected") as Checkbox;
            if(cb) {
                cb.enabled = false;
                cb.disabledTooltip = "A carrier email is required";
            }
        }
        AvailableLoads.displayOfferIcon(tableRow, modelRow.get("carrier_offer.id"));

        if (!StringUtil.isEmptyString(modelRow.get("carrier_offer.id")))
            (tableRow.findComponentById("imageQuickOffer") as Image).visible = false;

        const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
        if (!modelRow.isNull("movement_order.planned_manifest_id")) {
            orderIdLabel.visible = false;
            panelCommodity.visible = false;
            (tableRow.findComponentById("textboxEquipmentType") as Textbox).visible = false;
            const manifestIdLabel = tableRow.findComponentById("labelMovementOrderplannedManifestId") as Label;
            if (manifestIdLabel != null)
                manifestIdLabel.addUnderlineWhenMouseOver();
            if (modelRow.getBoolean("manifest_hold") === true) {
                (tableRow.findComponentById("imageManifestHold") as Image).visible = true;
                const quickoffer = (tableRow.findComponentById("imageQuickOffer") as Image);
                quickoffer.enabled = false;
                quickoffer.color = "subtle.light";
                quickoffer.disabledTooltip = "The Quick Offer button is disabled because the manifest is on hold.";
                (tableRow.findComponentById("buttonAssignCarrier") as Button).enabled = false;
                (tableRow.findComponentById("buttonAssignCarrier") as Button).disabledTooltip = "The Assign Carrier button is disabled because the manifest is on hold.";
                tableRow.findComponentById("checkboxSelected").enabled = false;
            }
        }
        if (modelRow.isNull("manifest_equipment"))
            tableRow.findComponentById("textboxManifestEquipment").visible = false;
        if (modelRow.isNull("manifest_commodities"))
            tableRow.findComponentById("textboxManifestCommodities").visible = false;
        if (isSpotOrder(modelRow.get("orders.subject_order_status"))) {
            (tableRow.findComponentById("buttonAssignCarrier") as Button).enabled = false;
            (tableRow.findComponentById("buttonAssignCarrier") as Button).disabledTooltip = "The Assign Carrier button is disabled because the order is a spot order.";
        }

    }

    checkboxSelectedOnChange(event: ChangeEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Checkbox);

        if (event.newValue) {
            tableRow.data.set("exclude_from_selectall", false);
        }

        if (event.userInitiatedChange)
            this.syncSelectAllCheckbox();
    }

    async buttonAssignCarrierOnClick(event: ClickEvent) {
        const assignButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        this.showAssignCarrierSlideout(tableRow, assignButton);
    }

    async showAssignCarrierSlideout(tableRow: TableRow, button: Button) {
        const modelRow = tableRow.data as ModelRow;
            const carrierOfferInfo: CarrierOfferInfo = {
                payee_id: this.carrierAssignmentProps.payee_id,
                carrier_name: this.carrierAssignmentProps.carrier_name,
                icc_number: this.carrierAssignmentProps.icc_number,
                dot_number: this.carrierAssignmentProps.dot_number,
                carrier_contact: this.carrierAssignmentProps.carrier_contact,
                carrier_phone: this.carrierAssignmentProps.carrier_phone,
                carrier_email: this.carrierAssignmentProps.carrier_email,
                broker_avail_tract: this.carrierAssignmentProps.broker_avail_tract
            }

            CarrierAssignmentStepper.showAsSlideout({
                movementId: modelRow.get("id"),
                orderId: modelRow.get("orders.id"),
                carrierAssignmentButton: button,
                carrierOfferInfo,
                onClose: (canceled: boolean) => {
                    if (!canceled) {
                        this.tabAvailableLoads.close();
                    }
                }
            });
    }

    formatDateFields(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        // Origin
        if (modelRow.getBoolean("origin_same_day_window")) {
            const originLateTime = tableRow.findComponentById("labelOriginLateTime") as Textbox;
            originLateTime.caption = " - " + originLateTime.caption;
            originLateTime.visible = modelRow.get("stop_origin.sched_arrive_late") != modelRow.get("stop_origin.sched_arrive_early");
        } else {
            tableRow.findComponentById("panelOriginLate").visible = modelRow.get("stop_origin.sched_arrive_late") != null;
        }

        // Destination
        if (modelRow.getBoolean("dest_same_day_window")) {
            const destLateTime = tableRow.findComponentById("labelDestLateTime") as Textbox;
            destLateTime.caption = " - " + destLateTime.caption;
            destLateTime.visible = modelRow.get("stop_dest.sched_arrive_late") != modelRow.get("stop_dest.sched_arrive_early");
        } else {
            tableRow.findComponentById("panelDestLate").visible = modelRow.get("stop_dest.sched_arrive_late") != null;
        }
    }

    cellStopsOnClick(event: ClickEvent) {
        this.showMultiStopSlideout(event);
    }

    async showMultiStopSlideout(event) {
        const label = event.target as Label;
        label.busy = true;
        if (!await Layout.canAccessLayout("lme/dispatch/MultiStop")) {
            label.busy = false;
            return;
        }
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        const isManifest = row.get("movement_order.planned_manifest_id") != null;
        const searchInput = isManifest ? { manifest_id: row.get("movement_order.planned_manifest_id"), movement_id: row.get("id") } : { id: row.get("orders.id") };
        let multiStopLayout: MultiStop;
        const erd = new EditRowDecorator({
            title: isManifest ? `Stop Details - Manifest ${row.get("movement_order.planned_manifest_id")}` : `Stop Details - Order ${row.get("orders.id")}`,
            layout: multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop,
            layoutLoadListeners: async (event) => {
                if (isManifest) {
                    multiStopLayout.mainDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.parentDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
                    multiStopLayout.isManifest = true;
                }

                await multiStopLayout.mainDataSource.search(searchInput);
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                multiStopLayout.activeRow.set("calc_target_pay", row.get("target_pay_calc"));
                multiStopLayout.activeRow.set("calc_max_pay", row.get("max_pay_calc"));
                const summaryData = multiStopLayout.mainDataSource.summaryData;
                if (row.get("target_pay_method") === "S") {
                    multiStopLayout.textboxTargetPay.captionVisible = false;
                    multiStopLayout.panelMPact.visible = true;
                }
                // Commodity Quick Info
                multiStopLayout.activeRow.set("order_id", row.get("orders.id"));
                setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
                if (isManifest) {
                    multiStopLayout.panelOrderInfo.visible = false;
                    multiStopLayout.panelManifestInfo.visible = true;
                }
                if (isManifest && summaryData?.get("customers").length === 1) {
                    multiStopLayout.labelManifestCustAddress1.visible = true;
                    multiStopLayout.citystateManifestCust.visible = true;
                }
            },
            width: '80%',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onSave: async () => {
                await multiStopLayout.postChanges();
                this.searchForLoads();
            },
            onClose: (cancelled: boolean) => multiStopLayout.slideOut(),
            doAfterSlideIn: () => label.busy = false,
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
    }

    textboxPiecesOnDataDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        textbox.text = textbox.getDataValue() + ` ${textbox.getDataValue() > 1 ? "pieces" : "piece"}`;
    }

    setPostedRateVisibility(tableRow: TableRow) {
        const textboxPostedRate = tableRow.findComponentById("textboxPnnRate") as Textbox;
        const panelPostedRate = tableRow.findComponentById("panelPostedRate") as Panel;
        panelPostedRate.visible = !StringUtil.isEmptyString(textboxPostedRate.getDataValue());
    }

    setTemperatureVisibility(tableRow: TableRow) {
        const minTemp = tableRow.findComponentById("labelMinTemp") as Label;
        const maxTemp = tableRow.findComponentById("labelMaxTemp") as Label;
        minTemp.visible = !StringUtil.isEmptyString(minTemp.getDataValue());
        if (!StringUtil.isEmptyString(maxTemp.getDataValue())) {
            maxTemp.visible = true;
            maxTemp.text = " to " + maxTemp.text;
        }
    }

    // START: Carrier Offer Functions
    private _offerLayoutLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return async (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            //Logic segmented by Sections in the Quick Offer Slideout to increase maintainability
            const loCarrierOffer = (rowDecorator.layout as QuickOfferSlideout);
            loCarrierOffer.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await loCarrierOffer.mainDataSource.createBlankRow()]);

            // Decorator Setup
            const orderId = tableRow.data.get("orders.id");
            rowDecorator.labelTitle.text = `Create Quick Offer - Order ${orderId}`;
            rowDecorator.btnDelete.visible = false;
            rowDecorator.multiButton.dropdownItems = null;
            rowDecorator.multiButton.primaryButton.paddingLeft = 7;
            rowDecorator.onSave = (_) => this.onSaveCallback(tableRow, loCarrierOffer);

            // Initial Data
            loCarrierOffer.activeRow._appending = true;
            const movementId = tableRow.data.get("id");
            const isSubjectOrder = tableRow.data.get("orders.subject_order_number", null) != null
                && tableRow.data.get("orders.subject_order_status", null) != "W";
            loCarrierOffer.activeRow.setValues({
                "order_id": orderId,
                "movement_id": movementId,
                "is_subject_order": isSubjectOrder,
                "offer_date": new Date(),
                "entered_user_id": LMEUserSettings.getUserId(),
                "source": OfferSource.QUICKOFFER,
                "electronic_offer": "N",
                "stop_origin.appt_required": tableRow.data.get("stop_origin.appt_required", null),
                "stop_origin.confirmed": tableRow.data.get("stop_origin.confirmed", null),
                "orders_on_hold": tableRow.data.get("orders.on_hold", null),
                "pending_payee": tableRow.data.get("pending_payee_id", tableRow.data.get("has_pending_assignment", false)),
            });

            // QuickSellDetails
            const quickSellDetails = loCarrierOffer.layoutQuickSellDetails as QuickSellDetails;
            const quickSellDS = new DataSource();
            const quickSellMR = tableRow.data.createBasicCopy();
            quickSellDS.setRowsAndMode(DataSourceMode.NONE, [quickSellMR]);
            quickSellDetails.replaceMainDatasource(quickSellDS);
            quickSellDetails.displayData(quickSellMR, null, 0);
            quickSellDetails.configureForSlideout(tableRow.data?.get("stops_pick_drop"));

            // General Information
            loCarrierOffer.activeRow.set("status", "I");
            loCarrierOffer.assignableCheck = () => DispatchUtil.isAssignable(tableRow.data, { btnAssign: loCarrierOffer.buttonAssignCarrier });
            loCarrierOffer.assignButtonClicked = () => rowDecorator.multiButton.primaryButton.clicked();
            loCarrierOffer.carrierAssigned = () => this.tabAvailableLoads.close();

            // Carrier Information
            loCarrierOffer.tbMcNumber.enabled = false;
            loCarrierOffer.tbCarrierName.enabled = false;
            loCarrierOffer.tbDotNumber.enabled = false;
            const carrierId = this.sourceCarrier.activeRow.get("payee.id", "");
            const carrierName = this.sourceCarrier.activeRow.get("payee.name", "");
            const carrierContact = this.sourceCarrier.activeRow.get("contact.name", "");

            loCarrierOffer.activeRow.setValues({
                "carrier_name": carrierName,
                "payee_id": carrierId,
                "equipment_type_id": tableRow.data.get("orders.equipment_type_id", null),
                "icc_number": this.sourceCarrier.activeRow.get("icc_number", ""),
                "dot_number": this.sourceCarrier.activeRow.get("dot_number", "")
            });
            loCarrierOffer.activeRow.setLookupModelData("equipment_type_id", new ModelRow(loCarrierOffer.tbTrailerType.lookupModel, false, {
                "id": tableRow.data.get("orders.equipment_type_id", null)
            }));
            loCarrierOffer.activeRow.setValueAndLookupModelData("carrier_name", carrierName, new ModelRow(loCarrierOffer.tbCarrierName.lookupModel, false, {
                "name": carrierName,
                "id": carrierId
            }));
            loCarrierOffer.activeRow.setLookupModelData("payee_id", new ModelRow(loCarrierOffer.tbCarrierName.lookupModel, false, {
                "name": carrierName,
                "id": carrierId
            }));

            //Carrier Contact
            loCarrierOffer.activeRow.setValues({
                "contact_name": carrierContact,
                "contact_phone": this.sourceCarrier.activeRow.get("calculated_phone", ""),
                "email": this.sourceCarrier.activeRow.get("contact.email", "")
            });
            loCarrierOffer.activeRow.setLookupModelData("contact_name", new ModelRow(loCarrierOffer.tbContactName.lookupModel, false, { "name": carrierContact }));

            //Rates
            const loCarrierPay = loCarrierOffer.loCarrierPay as MovementCarrierPay;
            loCarrierPay.buttonOverrideTargetPay.enabled = false;
            loCarrierPay.buttonOverrideMaxPay.enabled = false;
            await loCarrierPay.mainDataSource.search({ id: movementId });
            loCarrierPay.displayCalculatedFields();
            loCarrierPay.layoutTargetPay.setupMpact(tableRow.data);
            const targetPay = loCarrierPay.layoutTargetPay.activeRow.getCurrency("target_pay_calc");
            loCarrierOffer.activeRow.setValues({
                "order_total_chg": loCarrierPay.activeRow.getCurrency("orders.total_charge"),
                "max_pay": loCarrierPay.activeRow.getCurrency("max_pay_calc"),
                "target_pay": targetPay
            });

            if (carrierId != null) {
                await this.calculateCarrierRate(loCarrierOffer.activeRow, carrierId, movementId, orderId);
            }
            if (!loCarrierOffer.activeRow.isNull("amount")) {
                loCarrierOffer.labelAutoRated.visible = true;
                CommonTooltips.setTooltipContentLayout(
                    loCarrierOffer.labelAutoRated,
                    loCarrierOffer.activeRow.get("carrier_rate_lane_id", null),
                    "lme/dispatch/rates/CarrierLaneRateQuickInfo"
                );
            } else {
                loCarrierOffer.activeRow.set("amount", targetPay);
            }

            loCarrierOffer.updateMargins(OfferField.OFFER, "offer_margin", loCarrierOffer.tbDispatchMargin, loCarrierOffer.tbDispatchMarginPrcnt, true);

            loCarrierOffer.mainDataSource.displayDataInBoundComponents();
        }
    }

    async calculateCarrierRate(modelRow: ModelRow, payeeId: string, movementId: string, orderId: string) {
        const response = await Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": movementId,
            "order_id": orderId,
            "payee_id": payeeId,
            "auto_rate": LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y",
            "calculate_fuel_surcharge": true
        });
        if (response.data[0]?.carrier_rate_lane_id) {
            //Had to create new currency for each because of passing by reference issues
            const amount = new Currency(response.data[0]?.total_pay);
            const carrier_rate = new Currency(response.data[0]?.total_pay);
            modelRow.set("amount", amount);
            modelRow.set("carrier_rate", carrier_rate);
            modelRow.set("carrier_rate_id", response.data[0]?.carrier_rate_id);
            modelRow.set("carrier_rate_lane_id", response.data[0]?.carrier_rate_lane_id);
        } else {
            modelRow.set("carrier_rate", null);
        }
    }

    onSaveCallback = async (tableRow: TableRow, loCarrierOffer: QuickOfferSlideout) => {
        const postedOffer = await loCarrierOffer.mainDataSource.post();
        if (postedOffer) {
            tableRow.data.set("carrier_offer.id", postedOffer.get("id"));
            AvailableLoads.displayOfferIcon(tableRow, postedOffer.get("id"));
            if (this.validateAllOrdersHaveOffer() === true)
                this.disableBlast();
        } else
            log.error("Unable to save carrier offer");
    }

    static displayOfferIcon(tableRow: TableRow, offerId: string) {
        if (tableRow == null || offerId == null) return;
        const lblOffered = tableRow.findComponentById("lblOffered");
        lblOffered.visible = true;
        CommonTooltips.setTooltipContentLayout(lblOffered, offerId, "lme/powerbroker/CarrierOfferQuickInfo", { width: 225 });
        tableRow.findComponentById("checkboxSelected").enabled = false;
        (tableRow.findComponentById("imageQuickOffer") as Image).visible = false;
    }

    buttonBlastAllOffersOnClick(event: ClickEvent) {
        this.sendOffers();
    }

    async sendOffers(isWaterfall = false) {
        this.buttonBlastAllOffers.busy = true;
        // get everything that is being offered
        const offering = this.tableAvailableLoads.allRows.filter(tableRow => {
            if (!tableRow.validateSimple()) {
                tableRow.data.set("selected", false);
                return false;
            } else {
                return tableRow.data.getBoolean("selected", false);
            }
        });
        const offeringData: ModelRow[] = [];
        offering.forEach(tableRow => {
            if (tableRow.populatedDOM) {
                if (!tableRow.validateSimple()) return;
            }
            const offerCopy = this.createOfferCopy(tableRow);
            offerCopy.set("source", isWaterfall ? OfferSource.WATERFALL : OfferSource.BLAST);
            offeringData.push(offerCopy);
        });

        // post the offers
        let offerSent = false;
        let offerIds: object;
        if (offeringData.length > 0)
            await Api.post("lme/powerbroker/send-carrier-capacity-offers", {
                offers: offeringData,
                is_waterfall: isWaterfall,
                is_electronic_offer: true
            })
            .then((res) => {

                offerIds = res.data[0]?.offer_ids;
                offerSent = true;
            }).catch((err) => {
                CommonDialogs.showServerError(err);
            })
            .finally(() => {
                offering.forEach(tableRow => {
                    let offerId: string;
                    if (offerIds !== undefined)
                        offerId = offerIds[tableRow.data.get("id")]
                    if (offerSent && !StringUtil.isEmptyString(offerId)) {
                        tableRow.data.set("carrier_offer.id", offerId);
                        this.offerSentActions(tableRow, isWaterfall);
                        AvailableLoads.displayOfferIcon(tableRow, offerId);
                        tableRow.findComponentById("checkboxSelected").enabled = false;
                        if (this.validateAllOrdersHaveOffer() === true)
                            this.disableBlast();
                    }
                    if (tableRow.populatedDOM && tableRow.data.get("carrier_offer.id") === undefined)
                        this.getSelectedCheckbox(tableRow).enabled = true;
                    else if (tableRow.populatedDOM && tableRow.data.get("carrier_offer.id") !== undefined)
                        this.getSelectedCheckbox(tableRow).enabled = false;
                    this.getSelectedCheckbox(tableRow).checked = false;

                });
                this.buttonBlastAllOffers.busy = false;
                this.syncSelectAllCheckbox();
            })
    }

    getSelectedCheckbox(row: TableRow): Checkbox {
        return row.findComponentById("checkboxSelected") as Checkbox;
    }

    enableDisableOffers(tableRow: TableRow, value?: boolean) {
        // Determine whether offers should be enabled for this row
        const modelRow = tableRow.data as ModelRow;
        const waterfallInProgress = this.mainDataSource.activeRow?.getBoolean("waterfall_in_progress", false);
        if (waterfallInProgress) {
            modelRow.set("selected", false);
            value = false;
        }
        if (value == null) {
            value = this.canEnableCheckbox(tableRow, waterfallInProgress);
        }

        // Enable/disable the select checkbox
        if (!waterfallInProgress && modelRow.getBoolean("exclude_from_selectall", false)) {
            modelRow.set("selected", false); // skip over enabling/disabling the checkbox
        } else if (tableRow.populatedDOM) {
            this.getSelectedCheckbox(tableRow).enabled = value;
        }
        if (!waterfallInProgress)
            this.syncSelectAllCheckbox();
    }

    createOfferCopy(tableRow: TableRow): ModelRow {
        const offer = (tableRow.data as ModelRow).createBasicCopy();
        offer._appending = true;
        offer.set("movement_id", offer.get("id"));
        offer.set("order_id", offer.get("orders.id"));
        offer.set("id", null);
        offer.set("email", this.sourceCarrier.activeRow.get("contact.email"));
        offer.set("order_total_chg", offer.get("orders.total_charge"));
        const rate = this.mainDataSource.activeRow.get("orders.pnn_rate", this.mainDataSource.activeRow?.get("override_targetpay",
            this.mainDataSource.activeRow?.get("target_pay", null))) as Currency;
        offer.set("amount", rate);
        offer.set("status", "I");
        offer.set("dot_number", this.sourceCarrier.activeRow.get("dot_number", null));
        offer.set("icc_number", this.sourceCarrier.activeRow.get("icc_number", null));
        offer.set("payee_id", this.sourceCarrier.activeRow.get("id", null));
        offer.set("contact_phone", this.sourceCarrier.activeRow.get("contact.phone", null));
        offer.set("contact_name", this.sourceCarrier.activeRow.get("contact.name", null));
        offer.set("carrier_name", this.sourceCarrier.activeRow.get("payee.name", null));
        return offer;
    }

    offerSentActions(tableRow: TableRow, isWaterfall: boolean = false) {
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("exclude_from_selectall", true);
        modelRow.set("selected", false);

        this.syncSelectAllCheckbox();
    }

    syncSelectAllCheckbox() {
        let rowsAreSelected: boolean = undefined;
        if (this.tableAvailableLoads.rows.every(tableRow => {
            return !(tableRow.data.getBoolean("selected", false));
        })) { // check if all checkboxes are not checked
            rowsAreSelected = false;
        } else if (this.tableAvailableLoads.rows.every(tableRow => {
            return (!this.canEnableCheckbox(tableRow))
                ? true
                : (tableRow.data.getBoolean("exclude_from_selectall", false) || tableRow.data.getBoolean("selected", false));
        })) { // check if all checkboxes that can be checked are checked
            rowsAreSelected = true;
        }

        const canBlast: boolean = !StringUtil.isEmptyString(this.carrierAssignmentProps.carrier_email) && this.tableAvailableLoads.allRows.length != 0
        && this.tableAvailableLoads.rows.some(tableRow => tableRow.data.isNull("carrier_offer.id"));
        this.checkboxSelectAll.enabled = canBlast;
        this.checkboxSelectAll.checked = rowsAreSelected;
        this.buttonBlastAllOffers.enabled = canBlast && rowsAreSelected !== false;
        this.buttonBlastAllOffers.backgroundColor = canBlast && rowsAreSelected !== false ? "primary" : "primary.lightest";
    }

    canEnableCheckbox(tableRow: TableRow, waterfallInProgress: boolean = false): boolean {
        const modelRow = tableRow.data as ModelRow;
        const email = this.sourceCarrier.activeRow.get("contact.email", null);
        const disableElectronicOffers = modelRow.getBoolean("disable_electronic_offers", false);
        const offerId = modelRow.get("carrier_offer.id", null);
        const manifestOnHold = modelRow.getBoolean("manifest_hold", false);
        return !StringUtil.isEmptyString(email) && this.validEmail(email) && !disableElectronicOffers && !waterfallInProgress && StringUtil.isEmptyString(offerId) && !manifestOnHold;
    }

    private validEmail(email: string): boolean {
        return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+$/.test(email);
    }

    validateAllOrdersHaveOffer(): boolean {
        for (const row of this.tableAvailableLoads.rows) {
            if (!row.data.get("carrier_offer.id"))
                return false;
        }
        return true;
    }

    disableBlast() {
        this.buttonBlastAllOffers.enabled = false;
        this.buttonBlastAllOffers.backgroundColor = "primary.lightest";
        if (this.checkboxSelectAll)
            this.checkboxSelectAll.enabled = false;
    }

    imageQuickOfferOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Image);
        const basicCopy = tableRow.data.createBasicCopy();
        const erd = new EditRowDecorator({
            layout: Layout.getLayout(this.tableAvailableLoads.editLayout),
            data: basicCopy,
            layoutLoadListeners: (event) => this.tableAvailableLoads.onEditLayoutLoaded(erd, tableRow),
            width: this.tableAvailableLoads.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            dataSourceMode: tableRow.mode === TableRowMode.SEARCH ? DataSourceMode.SEARCH : DataSourceMode.UPDATE,
            onDelete: tableRow.mode !== TableRowMode.QUICK_ADD ? () => {
                tableRow.deleteRow(() => erd.slideOut());
            } : null,
            onSave: (updatedData: ModelRow | any) => tableRow.saveChangesFromEdit(updatedData),
            onAddAnother: () => tableRow.editClearNewRow(erd),
            onClose: (cancelled: boolean) => tableRow.onSlideoutClose(erd, cancelled, false)
        });
    }

    configureSelectColumn() {
        const column: TableColumn = this.tableAvailableLoads.columns.find(col => "colHeaderSelected" === col.headingCell.id);
        const headingCell: TableCell = column.headingCell;
        this.checkboxSelectAll = new Checkbox({
            id: "checkboxSelectAll",
            align: HorizontalAlignment.LEFT,
            color: "primary",
            checkSize: 32,
            padding: 0,
            onClick: (event) => this.checkboxSelectAllOnClick(this.tableAvailableLoads)
        });
        if (StringUtil.isEmptyString(this.sourceCarrier.activeRow.get("contact.email"))) {
            this.checkboxSelectAll.enabled = false;
            this.checkboxSelectAll.disabledTooltip = "A carrier email is required";
        }
        this.checkboxSelectAll.allowIndeterminate = true;
        headingCell.components = [];
        headingCell.add(this.checkboxSelectAll);
    }

    checkboxSelectAllOnClick(table: Table) {
        if (this.checkboxSelectAll.checked || this.checkboxSelectAll.checked == undefined) {
            this.checkboxSelectAll.checked = true;
            table.allRows.forEach(tableRow => {
                const modelRow = tableRow.data as ModelRow;
                if (modelRow.isNull("carrier_offer.id") && modelRow.getBoolean("manifest_hold") === false) {
                    modelRow.set("selected", true);
                }
            });
        } else {
            table.allRows.forEach(tableRow => {
                const modelRow = tableRow.data as ModelRow;
                modelRow.set("selected", false);
            });
        }
        this.syncSelectAllCheckbox();
    }

    manifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("movement_order.planned_manifest_id"), { newTab: true });
    }
}
